import { notify, useConfirmModal } from '@pique/component';
import { authAtom } from '@pique/core/atoms';
import { AuthService, GraphQLService } from '@pique/core/modules/cloud';
import { WorkspaceSubPath } from '@pique/core/shared';
import { setActiveWorkspaceMutation } from '@pique/graphql';
import { useI18n } from '@pique/i18n';
import type { Workspace } from '@toeverything/infra';
import {
  useLiveData,
  useService,
  WorkspacesService,
} from '@toeverything/infra';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { useNavigateHelper } from '../use-navigate-helper';

interface ConfirmEnableCloudOptions {
  /**
   * Fired when the workspace is successfully enabled
   */
  onSuccess?: () => void;
  /**
   * Fired when workspace is successfully enabled or user cancels the operation
   */
  onFinished?: () => void;
  openPageId?: string;
}
type ConfirmEnableArgs = [Workspace, ConfirmEnableCloudOptions | undefined];

export const useEnableCloud = () => {
  const t = useI18n();
  const authService = useService(AuthService);
  const account = useLiveData(authService.session.account$);
  const loginStatus = useLiveData(useService(AuthService).session.status$);
  const setAuthAtom = useSetAtom(authAtom);
  const { openConfirmModal, closeConfirmModal } = useConfirmModal();
  const workspacesService = useService(WorkspacesService);
  const graphQLService = useService(GraphQLService);
  const { openPage } = useNavigateHelper();

  const enableCloud = useCallback(
    async (ws: Workspace | null, options?: ConfirmEnableCloudOptions) => {
      try {
        if (!ws) return;
        if (!account) return;
        const { id: newId } = await workspacesService.transformLocalToCloud(
          ws,
          account.id
        );
        // Persist immediately so bootstrap/workspace-page never loses track
        try {
          localStorage.setItem('last_workspace_id', newId);
        } catch {}
        graphQLService
          .gql({
            query: setActiveWorkspaceMutation,
            variables: { workspaceId: newId },
          })
          .catch(() => {});
        // Wait for the workspace list to include the new cloud workspace
        // before navigating, so the workspace page doesn't hit "not found"
        const abort = new AbortController();
        const timeout = setTimeout(() => abort.abort(), 10000);
        try {
          await workspacesService.list.waitForRevalidation(abort.signal);
          await workspacesService.list.workspaces$
            .waitFor(
              list => list.some(w => w.id === newId),
              abort.signal
            );
        } catch {
          // Timed out or aborted — navigate anyway, bootstrap will recover
        } finally {
          clearTimeout(timeout);
        }
        openPage(newId, options?.openPageId || WorkspaceSubPath.ALL);
        options?.onSuccess?.();
      } catch (e) {
        console.error(e);
        notify.error({
          title: t['com.pique.workspace.enable-cloud.failed'](),
        });
      }
    },
    [account, graphQLService, openPage, t, workspacesService]
  );

  const openSignIn = useCallback(() => {
    setAuthAtom(prev => ({ ...prev, openModal: true }));
  }, [setAuthAtom]);

  const signInOrEnableCloud = useCallback(
    async (...args: ConfirmEnableArgs) => {
      // not logged in, open login modal
      if (loginStatus === 'unauthenticated') {
        openSignIn();
      }

      if (loginStatus === 'authenticated') {
        await enableCloud(...args);
      }
    },
    [enableCloud, loginStatus, openSignIn]
  );

  const confirmEnableCloud = useCallback(
    (ws: Workspace, options?: ConfirmEnableCloudOptions) => {
      const { onSuccess, onFinished } = options ?? {};

      const closeOnSuccess = () => {
        closeConfirmModal();
        onSuccess?.();
      };

      openConfirmModal(
        {
          title: t['Enable Pique'](),
          description: t['Enable Pique Description'](),
          cancelText: t['com.pique.enableAffineCloudModal.button.cancel'](),
          confirmText:
            loginStatus === 'authenticated'
              ? 'Sync Notes'
              : t['Sign in and Enable'](),
          confirmButtonOptions: {
            variant: 'primary',
            ['data-testid' as string]: 'confirm-enable-affine-cloud-button',
            style: {
              borderRadius: '16px',
              padding: '6px 20px',
              fontSize: '13px',
              fontWeight: 500,
              background: 'var(--pique-green-gradient-light)',
              color: 'var(--affine-text-primary-color)',
              border: '1px solid var(--pique-green-border)',
              boxShadow: 'var(--pique-green-shadow-subtle)',
            },
          },
          onConfirm: async () =>
            await signInOrEnableCloud(ws, {
              ...options,
              onSuccess: closeOnSuccess,
            }),
          onOpenChange: (open: boolean) => {
            if (!open) onFinished?.();
          },
        },
        {
          autoClose: false,
        }
      );
    },
    [closeConfirmModal, loginStatus, openConfirmModal, signInOrEnableCloud, t]
  );

  return confirmEnableCloud;
};
